import { components } from "../types/api";

type CampaignStatus =
  components["schemas"]["_36_Enums.AssociatedCampaignStatus"];

interface StatusCampaign {
  status?: string | null;
  sourceId?: string | null;
  internalCampaignId?: string | null;
  sync?: {
    isLinked?: boolean | null;
    internalCampaignId?: string | null;
  } | null;
}

/**
 * Connected = receives API metrics (has a source ID or is linked). Manual
 * campaigns (direct buys) never do, so their status is driven by hand only.
 */
export const isConnectedCampaign = (campaign: StatusCampaign): boolean =>
  Boolean(campaign.sourceId?.trim()) ||
  Boolean(campaign.internalCampaignId) ||
  Boolean(campaign.sync?.isLinked) ||
  Boolean(campaign.sync?.internalCampaignId);

interface StatusEditingInput {
  campaign: StatusCampaign;
  briefingStatus?: string | null;
  role?: string | null;
  canEditEverything: boolean;
  /** The user can edit the briefing's campaigns (not finalized/archived). */
  isEditMode: boolean;
}

export interface StatusEditing {
  status: CampaignStatus;
  editable: boolean;
  /** Statuses not offered in the menu. */
  excludedStatuses?: CampaignStatus[];
}

/** Whether (and to what) the user can change an associated campaign status. */
export const getCampaignStatusEditing = ({
  campaign,
  briefingStatus,
  role,
  canEditEverything,
  isEditMode,
}: StatusEditingInput): StatusEditing => {
  const status = (campaign.status ?? "DRAFT") as CampaignStatus;
  const isTechnician = role === "TECHNICIAN";
  const isAccount = role === "ACCOUNT";
  const isFinalized = briefingStatus === "FINALIZED";
  const isConnected = isConnectedCampaign(campaign);

  // A briefing is auto-finalized once every campaign is linked, while its
  // direct buys are still running, so manual ones stay editable there.
  const canEditManualInFinalized =
    isFinalized &&
    !isConnected &&
    (isTechnician || canEditEverything || isAccount);

  if (
    briefingStatus === "ARCHIVED" ||
    !(isTechnician || isEditMode || canEditManualInFinalized)
  ) {
    return { status, editable: false };
  }

  // Connected campaigns recalc their status automatically via a backend cron
  // (READY→ACTIVE→FINALIZED), so we don't expose any editing. But PENDING/DRAFT
  // are NOT recalculated ("excluida del recálculo automático hasta reanudarla"),
  // so they must stay manually editable even if a sourceId is present.
  const isAutoManaged =
    isConnected &&
    (isFinalized || (status !== "PENDING" && status !== "DRAFT"));
  if (isAutoManaged) return { status, editable: false };

  // Connected campaigns only become ACTIVE when metrics arrive.
  return {
    status,
    editable: true,
    excludedStatuses: isConnected ? ["ACTIVE"] : undefined,
  };
};
